import React, { useState, useEffect } from "react";
import { Node } from "reactflow";
import { useForm } from "react-hook-form";
import { XMarkIcon, EyeIcon } from "@heroicons/react/24/outline";
import { CustomNodeData, NodeType, Form, TaskFormData } from "../../types";
import { apiService } from "../../services/apiService";
import { User } from "../../types";
import { FormViewer } from "../forms/FormViewer";
import { useTranslation } from "react-i18next";

interface NodeEditorModalProps {
  node: Node<CustomNodeData>;
  onSave: (data: CustomNodeData) => void;
  onDelete: () => void;
  onClose: () => void;
}

export const NodeEditorModal: React.FC<NodeEditorModalProps> = ({
  node,
  onSave,
  onDelete,
  onClose,
}) => {
  const { t } = useTranslation(["workflows", "common"]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedForm, setSelectedForm] = useState<TaskFormData | null>(null);
  const [showFormViewer, setShowFormViewer] = useState(false);

  const { register, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      label: node.data.label,
      description: node.data.description || "",
      assigneeId: node.data.assigneeId || "",
      notificationMessage: node.data.notificationMessage || "",
      approvalRequired: node.data.approvalRequired || false,
      dueDate: node.data.dueDate || "",
      budgetThreshold: node.data.budgetThreshold || "",
      archiveConfirmation: node.data.archiveConfirmation || false,
    },
  });

  const nodeType = node.type as NodeType;
  const nodeTypeLabelMap: Record<NodeType, string> = {
    [NodeType.START]: t("workflows:nodeTypes.start"),
    [NodeType.END]: t("workflows:nodeTypes.end"),
    [NodeType.ACTIVITY]: t("workflows:nodeTypes.activity"),
    [NodeType.POOL_ACTIVITY]: t("workflows:nodeTypes.poolActivity"),
  };
  const nodeTypeLabel = nodeTypeLabelMap[nodeType] || nodeType;

  useEffect(() => {
    // Load users for assignment
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const userList = await apiService.getUsers();
      setUsers(userList);
    } catch (error) {
    }
  };

  const handleViewForm = (formData: any, index: number) => {
    // Convert form data to TaskFormData format for FormViewer
    const taskFormData: TaskFormData = {
      formName: formData.title || `Form ${index + 1}`,
      formDescription: formData.description || "",
      elements: formData.formStructure?.elements || [],
      elementOrder: JSON.stringify(formData.formStructure?.elementOrder || []),
      submissionData: formData.submissionData || {},
    };

    setSelectedForm(taskFormData);
    setShowFormViewer(true);
  };

  const handleCloseFormViewer = () => {
    setShowFormViewer(false);
    setSelectedForm(null);
  };

  const onSubmit = (data: any) => {
    const assignee = users.find((u) => u.id.toString() === data.assigneeId);

    const nodeData: CustomNodeData = {
      label: data.label,
      description: data.description,
      assigneeId: data.assigneeId ? parseInt(data.assigneeId) : undefined,
      assigneeName: assignee
        ? `${assignee.firstName} ${assignee.lastName}`
        : undefined,
      notificationMessage: data.notificationMessage,
      approvalRequired: data.approvalRequired,
      dueDate: data.dueDate,
      budgetThreshold: data.budgetThreshold,
      archiveConfirmation: data.archiveConfirmation,
    };

    onSave(nodeData);
  };

  const renderNodeSpecificFields = () => {
    switch (nodeType) {
      case NodeType.ACTIVITY:
        return (
          <>
            <div>
              <label className="form-label">{t("workflows:assignee")}</label>
              <select {...register("assigneeId")} className="form-input w-full">
                <option value="">{t("workflows:selectAssignee")}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">{t("workflows:dueDate")}</label>
              <input
                type="datetime-local"
                {...register("dueDate")}
                className="form-input w-full"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register("approvalRequired")}
                  className="rounded"
                />
                <span className="text-sm">{t("workflows:approvalRequired")}</span>
              </label>
            </div>

            {/* Activity Forms List */}
            {node.data.taskFormData?.multipleFormsData && node.data.taskFormData.multipleFormsData.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <label className="form-label">
                  {t("workflows:associatedForms", {
                    count: node.data.taskFormData.multipleFormsData.length,
                  })}
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {node.data.taskFormData.multipleFormsData.map((formData, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{formData.title}</h4>
                          {formData.description && (
                            <p className="text-sm text-gray-600 mt-1">{formData.description}</p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                            <span>
                              {t("workflows:fieldsCount", {
                                count: formData.formStructure?.elements?.length || 0,
                              })}
                            </span>
                            {formData.isFromLibrary && (
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {t("workflows:formLibrary")}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleViewForm(formData, index)}
                          className="ml-3 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t("workflows:viewForm")}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );

      case NodeType.START:
        return (
          <div>
            <p className="text-sm text-gray-600">
              {t("workflows:startNodeHint")}
            </p>
          </div>
        );

      case NodeType.END:
        return (
          <div>
            <p className="text-sm text-gray-600">
              {t("workflows:endNodeHint")}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-semibold">
            {t("workflows:editNodeTitle", { node: nodeTypeLabel })}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div>
            <label className="form-label">
              {t("workflows:nodeLabel")} *
            </label>
            <input
              type="text"
              {...register("label", { required: true })}
              className="form-input w-full"
              placeholder={t("workflows:nodeLabelPlaceholder")}
            />
          </div>

          <div>
            <label className="form-label">{t("common:description")}</label>
            <textarea
              {...register("description")}
              className="form-input w-full h-20"
              placeholder={t("workflows:nodeDescriptionPlaceholder")}
            />
          </div>

          {renderNodeSpecificFields()}

          <div className="flex justify-between pt-4">
            <button type="button" onClick={onDelete} className="btn-danger">
              {t("workflows:deleteNode")}
            </button>

            <div className="space-x-2">
              <button type="button" onClick={onClose} className="btn-secondary">
                {t("common:cancel")}
              </button>
              <button type="submit" className="btn-primary">
                {t("common:save")}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>

      {/* Form Viewer Modal */}
      {showFormViewer && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg w-full h-full max-w-7xl max-h-[95vh] flex flex-col">
            <FormViewer
              formData={selectedForm}
              onClose={handleCloseFormViewer}
              title={selectedForm.formName}
            />
          </div>
        </div>
      )}
    </>
  );
};
